import React, { useState, useMemo } from 'react';
import { useGetAllLandingQuery } from '../store/api/userApiSlice';
import Navbar from '../components/Navbar/Navbar';
import Footer from '../components/Footer/Footer';
import SearchBar from '../components/Blog/SearchBar';
import BlogCard from '../components/Blog/BlogCard';
import { IoFilterOutline } from "react-icons/io5";
import './BlogPage.css';
import blogimg from '../assets/images/blogimg.png';
import shadowSvg from '../assets/images/Shadow.svg';

const BlogPage = () => {
  // API Query
  const { data, isLoading, error } = useGetAllLandingQuery();
  
  // Robust data extraction: API returns [{ blog: [...] }]
  const rawPosts = useMemo(() => {
    if (!data) return [];
    // Check if data is array and has at least one element with 'blog' property
    if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0].blog)) {
      return data[0].blog;
    }
    // Fallback/Generic checks just in case structure changes
    if (Array.isArray(data)) return data;   
    return [];
  }, [data]);

  // Local state
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Extract Categories dynamically
  const categories = useMemo(() => {
    const cats = new Set(rawPosts.map(p => p.category).filter(Boolean));
    return ['All', ...Array.from(cats)];
  }, [rawPosts]);

  // Filter Logic
  const filteredPosts = useMemo(() => {
    return rawPosts.filter(post => {
      // Search Filter
      const matchesSearch = searchTerm 
        ? (post.title?.toLowerCase().includes(searchTerm.toLowerCase()) || 
           post.description?.toLowerCase().includes(searchTerm.toLowerCase()))
        : true;

      // Category Filter
      const matchesCategory = selectedCategory === 'All' 
        ? true 
        : post.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [rawPosts, searchTerm, selectedCategory]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setShowFilterMenu(false);
  };

  return (
    <div className="blog-page">
      <Navbar />
      
      {/* Hero Section */}
      <div className="blog-hero">
        <div className="blog-hero-content">
          <h1 className="blog-hero-title">Where knowledge<br />meets opportunity.</h1>
          <p className="blog-hero-subtitle">
            Your career deserves clarity and confidence. Dive into stories, strategies, and insights that help you make smarter career choices, build new skills, and stay inspired while chasing your goals.
          </p>
          
          <div className="blog-hero-search">
            <SearchBar 
              placeholder="Search by topic or by name" 
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          
          <p className="blog-hero-popular">
            Popular searches: <span className="highlight">Job Preparation</span>, Latest Skills, Personal development, Select Career
          </p>
        </div>
        
        <div className="blog-hero-image-container">
           <img src={shadowSvg} alt="Shadow" className="blog-hero-shadow" />
           <img 
            src={blogimg} 
            alt="Blog Hero" 
            className="blog-hero-img" 
           />
        </div>
      </div>

      {/* Blog Content */}
      <div className="blog-content-container">
        <div className="blog-header-row">
          <h2 className="section-title">
            {searchTerm ? `Search Results (${filteredPosts.length})` : 'Latest Blogs'}
          </h2>
          <div className="filter-container">
            <button className="filter-btn" onClick={() => setShowFilterMenu(!showFilterMenu)}>
              <IoFilterOutline className="filter-icon" /> FILTERS
            </button>
            {showFilterMenu && (
              <div className="filter-menu">
                {categories.map(category => (
                  <button
                    key={category}
                    className={`filter-option ${selectedCategory === category ? 'active' : ''}`}
                    onClick={() => handleCategorySelect(category)}
                  >
                    {category}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {selectedCategory !== 'All' && (
          <div className="active-filter-badge">
            <span>{selectedCategory}</span>
            <button onClick={() => handleCategorySelect('All')}>✕</button>
          </div>
        )}

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Loading blogs...</p>
          </div>
        ) : (
          <>
            <div className="blogs-grid">
              {filteredPosts.map(post => (
                <BlogCard 
                  key={post._id || post.id} 
                  blog={{
                    ...post,
                    id: post._id || post.id,
                    excerpt: post.description || post.content || '',
                    date: post.createdAt || post.date || new Date().toISOString(),
                    readTime: '5 min read', // Static for now
                    image: post.image || ''
                  }} 
                />
              ))}
            </div>

            {filteredPosts.length === 0 && (
              <div className="no-results">
                <p>No blogs found matching your search.</p>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BlogPage;
